import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { getCookie } from "../../../utils/cookieUtils";
import { useTheme } from "../../../context/ThemeContext";
import Cross from "../../../images/Builder/cross.svg";
import CrossDark from "../../../images/Builder/cross_dark.svg";

const API_URL =
  "https://lhijbdwvj4.execute-api.ap-south-1.amazonaws.com/default/getUpdatePositionWatchlist";

const mapExchangeToSegment = (exchange) => {
  switch ((exchange || "").toUpperCase()) {
    case "NSE":
      return "nse_cm";
    case "NFO":
      return "nse_fo";
    case "BSE":
      return "bse_cm";
    case "MCX":
      return "mcx_fo";
    default:
      return "nse_cm";
  }
};

const getNextAvailableSlot = (watchlistData) => {
  const usedSlots = Object.keys(watchlistData || {}).filter(
    (id) => id !== "last_updated"
  );
  for (let i = 1; i <= 7; i++) {
    if (!usedSlots.includes(i.toString()) || !watchlistData[i.toString()]?.name) {
      return i.toString();
    }
  }
  return null;
};

const AddMultipleToWatchlistModal = ({ isOpen, onClose, instruments }) => {
  const USER_ID = getCookie("client_id");
  const ACCESS_TOKEN = getCookie("access_token");
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [watchlists, setWatchlists] = useState([]);
  const [rawWatchlistData, setRawWatchlistData] = useState({});
  const [selectedWatchlistId, setSelectedWatchlistId] = useState("");
  const [createMode, setCreateMode] = useState(false);
  const [newWatchlistName, setNewWatchlistName] = useState("");
  const [addingProgress, setAddingProgress] = useState({ current: 0, total: 0 });

  const preparedSymbols = useMemo(() => {
    if (!instruments || instruments.length === 0) return [];
    return instruments.map(instrument => ({
      trading_symbol: instrument.tradingSymbol || instrument.trading_symbol || instrument.symbol || "",
      exch: instrument.exchange || instrument.exch || "NSE",
      code: instrument.code,
      formatted_ins_name:
        instrument.formatted_ins_name || instrument.instrument_name || instrument.companyName || instrument.name || instrument.tradingSymbol || "",
      expiry: instrument.expiry || instrument.expiry_date || "null",
      symbol_name: instrument.symbol_name || instrument.underlying_symbol || instrument.symbol || instrument.tradingSymbol || "",
      lot_size: instrument.lot_size || "1",
      tick_size: instrument.tick_size || "0.01",
      strike_price: instrument.strike || instrument.strike_price || "null",
      segment_type: instrument.segment_type || mapExchangeToSegment(instrument.exchange),
    }));
  }, [instruments]);

  const checkAllInstrumentsExist = (watchlistData, instrumentCodes) => {
    if (!watchlistData || !instrumentCodes || instrumentCodes.length === 0) return false;
    const instruments = watchlistData.instruments || [];
    const existingCodes = new Set(instruments.map(inst => inst.code));
    // Check if ALL instruments already exist in this watchlist
    return instrumentCodes.every(code => existingCodes.has(code));
  };

  const fetchWatchlists = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        API_URL,
        { user_id: USER_ID, access_token: ACCESS_TOKEN, operation: "get" },
        { headers: { "Content-Type": "application/json" } }
      );
      if (response.data.statusCode === 200) {
        const data = response.data.watchlist || {};
        setRawWatchlistData(data);
        const instrumentCodes = preparedSymbols.map(s => s.code);
        const list = Object.keys(data)
          .filter((id) => id !== "last_updated")
          .filter((id) => data[id].name && data[id].name.trim())
          .map((id) => ({
            id,
            name: data[id].name,
            alreadyAdded: checkAllInstrumentsExist(data[id], instrumentCodes)
          }));
        setWatchlists(list);
      }
    } catch (e) {
      // no-op minimal
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setCreateMode(false);
      setNewWatchlistName("");
      setAddingProgress({ current: 0, total: 0 });
      fetchWatchlists();
    }
  }, [isOpen]);

  const handleCreateThenAdd = async () => {
    if (!newWatchlistName.trim()) return;
    const nextSlot = getNextAvailableSlot(rawWatchlistData);
    if (!nextSlot) {
      toast("Maximum watchlists limit reached!", {
        position: "bottom-center",
        autoClose: 1000,
        theme: "dark",
        className: "custom-snackbar",
        bodyClassName: "custom-snackbar-body",
      });
      return;
    }
    try {
      setLoading(true);
      const createResp = await axios.post(
        API_URL,
        {
          user_id: USER_ID,
          access_token: ACCESS_TOKEN,
          operation: "create",
          page: nextSlot,
          list_name: newWatchlistName.trim(),
        },
        { headers: { "Content-Type": "application/json" } }
      );
      if (!(createResp.data.success || createResp.data.statusCode === 200)) {
        throw new Error(createResp.data.errorMessage || "Failed to create watchlist");
      }

      await handleAddAllToWatchlist(nextSlot);
    } catch (err) {
      toast.error(err.message || "Failed to create watchlist");
      setLoading(false);
    }
  };

  const handleAddAllToWatchlist = async (watchlistId) => {
    if (!preparedSymbols?.length || !watchlistId) return;

    try {
      setLoading(true);

      const instruments = preparedSymbols.map(symbol => ({
        trading_symbol: symbol.trading_symbol,
        exchange: symbol.exch,
        code: symbol.code,
        formatted_ins_name: symbol.formatted_ins_name,
        expiry: symbol.expiry ?? null,
        name: symbol.symbol_name,
        lot_size: symbol.lot_size ?? 1,
        tick_size: symbol.tick_size ?? 0.01,
        strike_price: symbol.strike_price ?? null,
        segment_type: symbol.segment_type,
      }));

      const body = {
        user_id: USER_ID,
        access_token: ACCESS_TOKEN,
        operation: "add_multiple", // ✅ MATCH FLUTTER
        page: watchlistId,
        instruments,
      };

      const response = await axios.post(
        "https://lhijbdwvj4.execute-api.ap-south-1.amazonaws.com/default/getUpdatePositionWatchlist",
        body,
        { headers: { "Content-Type": "application/json" } }
      );

      const data = response.data;

      if (!data?.success) {
        throw new Error(data?.errorMessage || "Failed to add stocks");
      }

      toast(`${instruments.length} stock${instruments.length > 1 ? "s" : ""} added to watchlist`, {
        position: "bottom-center",
        autoClose: 2000,
        hideProgressBar: true,
        theme: "dark",
      });

      setSelectedWatchlistId("");
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to add stocks to watchlist");
    } finally {
      setLoading(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[#0000004d] bg-opacity-50 flex items-center justify-center z-50">
      <div className="modal-box p-6 rounded-xl text-center bg-modalBg max-w-96">
        <div className="flex flex-col items-center">
          <div className="text-4xl">👋</div>
          <h3 className="font-medium text-[14px] mt-3 text-p300">
            {createMode ? "Create new watchlist" : "Select watchlist"}
          </h3>
          {!createMode && (
            <p className="text-[12px] font-medium text-n500">
              Add {preparedSymbols.length} stock{preparedSymbols.length > 1 ? 's' : ''} to watchlist
            </p>
          )}
          {addingProgress.total > 0 && (
            <p className="text-[12px] font-medium text-blue mt-2">
              Adding {addingProgress.current} of {addingProgress.total}...
            </p>
          )}
        </div>

        <div className="mt-4 space-y-4">
          {!createMode ? (
            <>
              {watchlists.length === 0 ? (
                <div className="text-sm text-n500 p-2">No watchlists yet</div>
              ) : (
                <div className="space-y-2 max-h-[320px] overflow-y-auto no-scrollbar">
                  {watchlists.map((w) => (
                    <label
                      key={w.id}
                      className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all cursor-pointer ${w.alreadyAdded
                        ? "bg-p400 border-n200 opacity-60 cursor-not-allowed"
                        : selectedWatchlistId === w.id
                          ? " bg-opacity-10 border-p300 border-2"
                          : "bg-p400 border-n200 hover:border-p300"
                        }`}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        {/* Native radio (kept for state + accessibility) */}
                        <input
                          type="radio"
                          name="watchlist"
                          value={w.id}
                          checked={selectedWatchlistId === w.id}
                          onChange={() => !w.alreadyAdded && setSelectedWatchlistId(w.id)}
                          disabled={loading || w.alreadyAdded}
                          className="sr-only"
                        />

                        {/* Custom radio */}
                        <div
                          onClick={() => !loading && !w.alreadyAdded && setSelectedWatchlistId(w.id)}
                          className={`flex items-center cursor-pointer ${loading || w.alreadyAdded ? "cursor-not-allowed" : ""
                            }`}
                        >
                          <div
                            className={`flex-shrink-0 w-4 h-4 rounded-full border-2 mr-2 flex items-center justify-center
        ${selectedWatchlistId === w.id
                                ? "border-[#0075FF]"
                                : "border-n500"
                              }
        ${w.alreadyAdded ? "opacity-50" : ""}
      `}
                          >
                            {selectedWatchlistId === w.id && (
                              <div className="w-2 h-2 rounded-full bg-[#0075FF]" />
                            )}
                          </div>

                          <span
                            className={`text-sm font-medium ${w.alreadyAdded ? "text-n500" : "text-p300"
                              }`}
                          >
                            {w.name}
                          </span>
                        </div>
                      </div>

                      {w.alreadyAdded && (
                        <span className="text-xs font-medium text-n500">
                          Already Added
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <button
                  className="border border-n200 px-4 py-2 rounded-lg "
                  onClick={() => {
                    setSelectedWatchlistId("");
                    onClose();
                  }}
                  disabled={loading}
                >
                  <img
                    src={theme === "dark" ? CrossDark : Cross}
                    alt="Close icon"
                    className="w-2 h-2"
                  />
                </button>
                <button
                  className="bg-swipe-button text-p400 px-4 py-2 rounded-lg w-full text-sm font-semibold disabled:opacity-50"
                  disabled={loading || !selectedWatchlistId}
                  onClick={() => handleAddAllToWatchlist(selectedWatchlistId)}
                >
                  {loading ? "Adding..." : "Continue"}
                </button>
              </div>
              {watchlists.length < 7 && (
                <div className="flex flex-col items-center gap-2">
                  <div className="w-full flex items-center gap-2">
                    <div className="flex-1 border-t border-n200"></div>
                    <div className="text-sm text-n500">Or</div>
                    <div className="flex-1 border-t border-n200"></div>
                  </div>

                  <button
                    className="text-sm underline"
                    onClick={() => setCreateMode(true)}
                    disabled={loading}
                  >
                    Create new
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <input
                type="text"
                className="w-full p-2 rounded-md bg-p400 text-p300 border border-n200 text-sm"
                placeholder="Name of the watchlist"
                value={newWatchlistName}
                onChange={(e) => setNewWatchlistName(e.target.value)}
                maxLength={20}
              />
              <div className="flex gap-2 mt-2">
                <button
                  className="border border-n200 px-4 py-2 rounded-lg text-sm"
                  onClick={() => setCreateMode(false)}
                  disabled={loading}
                >
                  Back
                </button>
                <button
                  className="bg-swipe-button text-p400 px-4 py-2 rounded-lg text-sm font-semibold w-full disabled:opacity-50"
                  disabled={loading || !newWatchlistName.trim()}
                  onClick={handleCreateThenAdd}
                >
                  {loading ? "Creating..." : "Create and add"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddMultipleToWatchlistModal;
